import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import AdminLayout from './AdminLayout';
import Breadcrumb from '../../components/common/Breadcrumb';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://vmc-backend-vt2w.onrender.com/api';

const summaryCards = [
  {
    key: 'totalBusinesses',
    title: 'Total Businesses',
    description: 'All registered business profiles',
    accent: 'text-slate-900',
  },
  {
    key: 'publishedBusinesses',
    title: 'Published Businesses',
    description: 'Currently visible on public listings',
    accent: 'text-emerald-700',
  },
  {
    key: 'pendingBusinesses',
    title: 'Pending Verification',
    description: 'Business profiles waiting for admin action',
    accent: 'text-amber-700',
  },
  {
    key: 'totalConsumers',
    title: 'Total Consumers',
    description: 'Consumer accounts available to manage',
    accent: 'text-blue-700',
  },
  {
    key: 'pendingReviews',
    title: 'Pending Reviews',
    description: 'Reviews awaiting moderation by businesses',
    accent: 'text-rose-700',
  },
];

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const ratingStars = (rating) => {
  const rounded = Math.max(0, Math.min(5, Math.round(rating || 0)));
  return '★'.repeat(rounded) + '☆'.repeat(5 - rounded);
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [dashboard, setDashboard] = useState({
    summary: {
      totalBusinesses: 0,
      publishedBusinesses: 0,
      pendingBusinesses: 0,
      totalConsumers: 0,
      pendingReviews: 0,
    },
    recentReviews: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError('');

        const response = await fetch(`${API_BASE}/admin/dashboard`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to load admin dashboard data');
        }

        const payload = await response.json();
        setDashboard(payload.data || {
          summary: {
            totalBusinesses: 0,
            publishedBusinesses: 0,
            pendingBusinesses: 0,
            totalConsumers: 0,
            pendingReviews: 0,
          },
          recentReviews: [],
        });
      } catch (err) {
        console.error('Dashboard load error:', err);
        setError('Unable to load dashboard right now. Please refresh or try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-slate-600 mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AdminLayout title="Dashboard">
      <div className="space-y-6">
        <div>
          <Breadcrumb items={[{ label: 'Admin', href: '/admin/dashboard' }, { label: 'Dashboard' }]} />
          <div className="mt-2 flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 tracking-tight">Platform Control Center</h2>
              <p className="mt-1 text-sm text-slate-500">
                Live admin-access data across businesses, consumers, and recent platform reviews.
              </p>
            </div>
          </div>
        </div>

        <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
          {summaryCards.map((card) => (
            <article
              key={card.key}
              className="rounded-xl border border-slate-200 bg-white px-4 py-4 shadow-sm"
            >
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{card.title}</p>
              <p className={`mt-2 text-3xl font-bold ${card.accent}`}>
                {loading ? '...' : dashboard.summary[card.key]}
              </p>
              <p className="mt-1 text-xs text-slate-500">{card.description}</p>
            </article>
          ))}
        </section>

        <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Recent Reviews Across Businesses</h3>
              <p className="mt-0.5 text-xs text-slate-500">Latest 5 review submissions on the platform</p>
            </div>
          </div>

          {error ? (
            <div className="px-5 py-8 text-sm text-rose-600">{error}</div>
          ) : loading ? (
            <div className="space-y-3 px-5 py-5">
              {[1, 2, 3, 4, 5].map((item) => (
                <div key={item} className="h-14 animate-pulse rounded-lg bg-slate-100" />
              ))}
            </div>
          ) : dashboard.recentReviews.length === 0 ? (
            <div className="px-5 py-8 text-sm text-slate-500">No recent reviews found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-5 py-3">Business</th>
                    <th className="px-5 py-3">Reviewer</th>
                    <th className="px-5 py-3">Rating</th>
                    <th className="px-5 py-3">Review</th>
                    <th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard.recentReviews.map((review) => (
                    <tr
                      key={review.id}
                      onClick={() => navigate(`/admin/businesses/${review.business_id}/reviews`)}
                      className="cursor-pointer border-t border-slate-100 hover:bg-blue-50/50"
                    >
                      <td className="px-5 py-3 font-medium text-slate-900">{review.business_name}</td>
                      <td className="px-5 py-3 text-slate-700">{review.reviewer_name}</td>
                      <td className="px-5 py-3 text-amber-600">{ratingStars(review.rating)}</td>
                      <td className="px-5 py-3 text-slate-600">
                        {review.title || review.content || 'No review text'}
                      </td>
                      <td className="px-5 py-3">
                        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold capitalize text-slate-700">
                          {review.status}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-slate-500">{formatDateTime(review.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
